import { AppType } from "client/Api/Apps/common";
import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	ZenyaDocConnectionSelectOptionsModel,
	getZenyaDocConnections,
	ZenyaDocConnectionSelectOptionModel
} from "client/Api/Apps/EditApp/appParameters";
import { ZenyaConnectionDialog } from "client/Features/Settings/Connections/Zenya/ZenyaConnectionDialog";
import { ZenyaConnectionListItemModel } from "client/Api/ZenyaConfiguration/zenyaConfiguration";
import {
	SaveZenyaDocAppModel,
	EditZenyaDocAppViewModel,
	saveZenyaDocApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveZenyaDocAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("connectionId")
	.add(
		isNotNullNorUndefined()
	);

export type EditZenyaDocAppProps = {
	initialSettings: EditAppSettings<EditZenyaDocAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditZenyaDocApp = forwardRef(
	(
		props: EditZenyaDocAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState<boolean>(false);
		const [connections, setConnections] = useState<ZenyaDocConnectionSelectOptionsModel>({
			canCreateNew: false,
			options: []
		});

		const [formData, setFormData] = useState<SaveZenyaDocAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const connectionOptions: readonly SelectItem<number>[] = useMemo(
			(): readonly SelectItem<number>[] => {
				return connections.options
					.map(
						o => ({
							value: o.id,
							text: o.name
						})
					);
			},
			[connections.options]
		);

		const alertManager: AlertManager = useAlerts();

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveZenyaDocAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const addConnectionHandler = useCallback(
			(item: ZenyaConnectionListItemModel): void => {
				setConnections((newConnections: ZenyaDocConnectionSelectOptionsModel) => {
					const existingItemIndex: number = newConnections.options
						.findIndex(i => i.id === item.id);

					const options: ZenyaDocConnectionSelectOptionModel[] = [
						...newConnections.options
					];

					if (existingItemIndex === -1) {
						options.push(item);
					} else {
						options.splice(existingItemIndex, 1, item);
					}

					return {
						...newConnections,
						options
					};
				});

				formHandler.for("connectionId")
					.attachToSelect()
					.onChange(item.id);

			},
			[formHandler]
		);

		useEffect(
			(): () => void => {
				const cancellation = new AbortController();

				const loadZenyaDocConnections = async (
					signal: AbortSignal
				): Promise<void> => {
					try {
						const result: ZenyaDocConnectionSelectOptionsModel =
							await getZenyaDocConnections(signal);

						setConnections(result);
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}
						alertManager.showError(localize("_GeneralErrorMessage"));
					}
				};

				loadZenyaDocConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[alertManager]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveZenyaDocAppModel>(
			formHandler,
			saveZenyaDocApp,
			AppType.ZenyaDoc,
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connectionId", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<>
				<EditAppBase
					formHandler={formHandler}
					hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
					initialAppSettings={app}
					usersAndUserGroupsData={usersAndUserGroupsData}
					initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
				>
					<FormItem label={localize("_Connection")}>
						<InputRow>
							<InputRow.Stretch>
								<Select
									{...formHandler.for("connectionId")
										.attachToSelect()}
									options={connectionOptions}
									placeholder={localize("_SelectConnection")}
									menuTitle={localize("_Connection")}
								/>
							</InputRow.Stretch>
							{connections.canCreateNew && (
								<Button
									text={localize("_Add")}
									onClick={() => setIsConnectionDialogOpen(true)}
									kind="secondary"
								/>
							)}
						</InputRow>
						<ValidationError
							{...formHandler.for("connectionId").attachToValidationError()}
						/>
					</FormItem>
					<AppDestinationWindowSelectFormItem
						attachable={formHandler.for("destinationWindow")}
					/>
				</EditAppBase>
				{isConnectionDialogOpen && (
					<ZenyaConnectionDialog
						onAfterSave={addConnectionHandler}
						onClose={() => setIsConnectionDialogOpen(false)}
					/>
				)}
			</>
		);
	}
);

EditZenyaDocApp.displayName = "EditZenyaDocApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditZenyaDocAppViewModel>
): SaveZenyaDocAppModel {
	const {
		connectionId,
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		connectionId,
		destinationWindow,
	};
}
